import React, { useState, useEffect } from 'react';
import { frontendDb as db } from '../../lib/frontend-db';

interface WiesenInfoSettings {
  wiesen_info_title: string;
  wiesen_info_subtitle: string;
  wiesen_info_hero_image: string;
  wiesen_info_content: string;
  wiesen_info_contact: string;
  wiesen_info_gallery: string;
  wiesen_info_history: string;
  wiesen_info_activities: string;
  wiesen_imprint_content: string;
  wiesen_privacy_content: string;
  wiesen_footer_bg_color: string;
  wiesen_footer_text_color: string;
  wiesen_footer_link_color: string;
  wiesen_footer_link_hover_color: string;
  wiesen_back_to_stimmart_text: string;
}

const WiesenInfoPage = () => {
  const [settings, setSettings] = useState<WiesenInfoSettings>({
    wiesen_info_title: 'Liederkranz Wiesen',
    wiesen_info_subtitle: 'Tradition und Musik seit Generationen',
    wiesen_info_hero_image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200&h=600&fit=crop',
    wiesen_info_content: 'Der Liederkranz Wiesen ist ein traditionsreicher Gesangverein, der seit vielen Jahren die Musikkultur in unserer Region prägt. Mit Leidenschaft und Hingabe pflegen wir das deutsche Liedgut und bringen Menschen durch die Kraft der Musik zusammen.',
    wiesen_info_contact: 'Kontakt:\nLiederkranz Wiesen e.V.\nMusterstraße 123\n63831 Wiesen\nTelefon: 06050 / 987654\nE-Mail: info@liederkranz-wiesen.de',
    wiesen_info_gallery: JSON.stringify([
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=300&fit=crop'
    ]),
    wiesen_info_history: 'Unsere Geschichte reicht weit zurück und ist geprägt von musikalischen Höhepunkten und gesellschaftlichen Ereignissen. Über die Jahrzehnte haben wir uns zu einem wichtigen kulturellen Bestandteil der Gemeinde entwickelt.',
    wiesen_info_activities: 'Wir bieten regelmäßige Proben, Konzerte und gesellschaftliche Veranstaltungen. Neue Mitglieder sind herzlich willkommen und können jederzeit zu unseren Proben kommen.',
    wiesen_imprint_content: '',
    wiesen_privacy_content: '',
    wiesen_footer_bg_color: '#2d5a2f',
    wiesen_footer_text_color: '#ffffff',
    wiesen_footer_link_color: '#ffffff',
    wiesen_footer_link_hover_color: 'rgba(255, 255, 255, 0.8)',
    wiesen_back_to_stimmart_text: 'Entdecken Sie auch unseren Hauptverein StimmArt und erfahren Sie mehr über unsere gemeinsamen musikalischen Aktivitäten und Veranstaltungen.'
  });
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [newGalleryImage, setNewGalleryImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const siteSettings = await db.getSettings();
      if (siteSettings) {
        const wiesenSettings = {
          wiesen_info_title: (siteSettings as any).wiesen_info_title || settings.wiesen_info_title,
          wiesen_info_subtitle: (siteSettings as any).wiesen_info_subtitle || settings.wiesen_info_subtitle,
          wiesen_info_hero_image: (siteSettings as any).wiesen_info_hero_image || settings.wiesen_info_hero_image,
          wiesen_info_content: (siteSettings as any).wiesen_info_content || settings.wiesen_info_content,
          wiesen_info_contact: (siteSettings as any).wiesen_info_contact || settings.wiesen_info_contact,
          wiesen_info_gallery: (siteSettings as any).wiesen_info_gallery || settings.wiesen_info_gallery,
          wiesen_info_history: (siteSettings as any).wiesen_info_history || settings.wiesen_info_history,
          wiesen_info_activities: (siteSettings as any).wiesen_info_activities || settings.wiesen_info_activities,
          wiesen_imprint_content: (siteSettings as any).wiesen_imprint_content || settings.wiesen_imprint_content,
          wiesen_privacy_content: (siteSettings as any).wiesen_privacy_content || settings.wiesen_privacy_content,
          wiesen_footer_bg_color: (siteSettings as any).wiesen_footer_bg_color || settings.wiesen_footer_bg_color,
          wiesen_footer_text_color: (siteSettings as any).wiesen_footer_text_color || settings.wiesen_footer_text_color,
          wiesen_footer_link_color: (siteSettings as any).wiesen_footer_link_color || settings.wiesen_footer_link_color,
          wiesen_footer_link_hover_color: (siteSettings as any).wiesen_footer_link_hover_color || settings.wiesen_footer_link_hover_color,
          wiesen_back_to_stimmart_text: (siteSettings as any).wiesen_back_to_stimmart_text || settings.wiesen_back_to_stimmart_text,
        };
        setSettings(wiesenSettings);
        
        try {
          const gallery = JSON.parse(wiesenSettings.wiesen_info_gallery);
          setGalleryImages(Array.isArray(gallery) ? gallery : []);
        } catch {
          setGalleryImages([]);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Fehler beim Laden der Wiesen-Info Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedSettings = {
        ...settings,
        wiesen_info_gallery: JSON.stringify(galleryImages)
      };
      
      await db.updateSettings(updatedSettings);
      window.location.reload();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Fehler beim Speichern der Wiesen-Info Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const imageUrl = await db.storeImage(file);
      setSettings(prev => ({
        ...prev,
        wiesen_info_hero_image: imageUrl
      }));
    } catch (error) {
      console.error('Error uploading hero image:', error);
      setError('Fehler beim Hochladen des Hero-Bildes');
    }
  };

  const handleGalleryImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const imageUrl = await db.storeImage(file);
      setGalleryImages(prev => [...prev, imageUrl]);
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      setError('Fehler beim Hochladen des Galerie-Bildes');
    }
  };

  const removeGalleryImage = (index: number) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          Wiesen-Info Einstellungen wurden erfolgreich gespeichert!
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Liederkranz Wiesen Info-Seite</h2>
          <a
            href="/wiesen-info"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Seite anzeigen
          </a>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Grundeinstellungen */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Grundeinstellungen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Titel</label>
                <input
                  type="text"
                  value={settings.wiesen_info_title}
                  onChange={(e) => setSettings(prev => ({ ...prev, wiesen_info_title: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Untertitel</label>
                <input
                  type="text"
                  value={settings.wiesen_info_subtitle}
                  onChange={(e) => setSettings(prev => ({ ...prev, wiesen_info_subtitle: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  required
                />
              </div>
            </div>
          </div>

          {/* Hero-Bild */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Hero-Bild</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Hero-Bild hochladen</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleHeroImageUpload}
                className="mt-1 block w-full"
              />
              {settings.wiesen_info_hero_image && (
                <div className="mt-2">
                  <img
                    src={settings.wiesen_info_hero_image}
                    alt="Hero Vorschau"
                    className="w-full max-h-48 object-cover rounded"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Hauptinhalt */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Hauptinhalt</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Beschreibungstext</label>
              <textarea
                value={settings.wiesen_info_content}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_info_content: e.target.value }))}
                rows={6}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
                placeholder="Beschreiben Sie den Liederkranz Wiesen..."
              />
            </div>
          </div>

          {/* Geschichte */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Geschichte</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Geschichtstext</label>
              <textarea
                value={settings.wiesen_info_history}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_info_history: e.target.value }))}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
                placeholder="Erzählen Sie die Geschichte des Vereins..."
              />
            </div>
          </div>

          {/* Aktivitäten */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Aktivitäten</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Aktivitätentext</label>
              <textarea
                value={settings.wiesen_info_activities}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_info_activities: e.target.value }))}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
                placeholder="Beschreiben Sie die Aktivitäten des Vereins..."
              />
            </div>
          </div>

          {/* Galerie */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Galerie</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Neues Galerie-Bild hinzufügen</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleGalleryImageUpload}
                className="mt-1 block w-full"
              />
            </div>
            
            {galleryImages.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {galleryImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image}
                      alt={`Galerie ${index + 1}`}
                      className="w-full h-32 object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(index)}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Impressum */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Impressum</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Impressum-Inhalt</label>
              <textarea
                value={settings.wiesen_imprint_content}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_imprint_content: e.target.value }))}
                rows={10}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                placeholder="Geben Sie hier den Impressum-Text für die Wiesen-Seite ein..."
              />
            </div>
          </div>

          {/* Datenschutz */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Datenschutzerklärung</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Datenschutz-Inhalt</label>
              <textarea
                value={settings.wiesen_privacy_content}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_privacy_content: e.target.value }))}
                rows={10}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                placeholder="Geben Sie hier die Datenschutzerklärung für die Wiesen-Seite ein..."
              />
            </div>
          </div>

          {/* Footer-Farben */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Footer-Farben</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Hintergrundfarbe</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={settings.wiesen_footer_bg_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_bg_color: e.target.value }))}
                    className="w-12 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={settings.wiesen_footer_bg_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_bg_color: e.target.value }))}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                    placeholder="#2d5a2f"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Textfarbe</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={settings.wiesen_footer_text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_text_color: e.target.value }))}
                    className="w-12 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={settings.wiesen_footer_text_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_text_color: e.target.value }))}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Link-Farbe</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={settings.wiesen_footer_link_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_link_color: e.target.value }))}
                    className="w-12 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={settings.wiesen_footer_link_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_link_color: e.target.value }))}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Link-Hover-Farbe</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={settings.wiesen_footer_link_hover_color.replace('rgba(255, 255, 255, 0.8)', '#cccccc')}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_link_hover_color: e.target.value }))}
                    className="w-12 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    value={settings.wiesen_footer_link_hover_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, wiesen_footer_link_hover_color: e.target.value }))}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                    placeholder="rgba(255, 255, 255, 0.8)"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 p-4 border rounded-lg" style={{ backgroundColor: settings.wiesen_footer_bg_color }}>
              <p className="text-center mb-2" style={{ color: settings.wiesen_footer_text_color }}>
                Liederkranz Wiesen e.V. © Copyright 2025
              </p>
              <div className="flex justify-center space-x-4">
                <span style={{ color: settings.wiesen_footer_link_color }}>Impressum</span>
                <span style={{ color: settings.wiesen_footer_link_color }}>Datenschutz</span>
              </div>
              <p className="text-xs text-center mt-2 opacity-75" style={{ color: settings.wiesen_footer_text_color }}>
                Vorschau des Footers
              </p>
            </div>
          </div>

          {/* Zurück zu StimmArt Text */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Zurück zu StimmArt</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Beschreibungstext</label>
              <textarea
                value={settings.wiesen_back_to_stimmart_text}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_back_to_stimmart_text: e.target.value }))}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
                placeholder="Text für den Zurück zu StimmArt Abschnitt..."
              />
            </div>
          </div>

          {/* Kontakt */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Kontaktinformationen</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700">Kontakttext</label>
              <textarea
                value={settings.wiesen_info_contact}
                onChange={(e) => setSettings(prev => ({ ...prev, wiesen_info_contact: e.target.value }))}
                rows={6}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                required
                placeholder="Kontakt:&#10;Liederkranz Wiesen e.V.&#10;Musterstraße 123&#10;63831 Wiesen&#10;Telefon: 06050 / 987654&#10;E-Mail: info@liederkranz-wiesen.de"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WiesenInfoPage;

